import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ShieldCheck, Video, RefreshCw, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";

const CUES = [
    "Look straight at the camera",
    "Blink twice slowly 👀",
    "Turn your head slightly left, then right",
    "Smile — you're almost done ✨",
];

const RECORD_MS = 4000;

export default function VerificationModal({ open, onClose, onVerified }) {
    const videoRef = useRef(null);
    const streamRef = useRef(null);
    const recorderRef = useRef(null);
    const chunksRef = useRef([]);
    const [status, setStatus] = useState("idle"); // idle | ready | recording | uploading | done | error
    const [cueIdx, setCueIdx] = useState(0);
    const [countdown, setCountdown] = useState(RECORD_MS / 1000);
    const [previewBlob, setPreviewBlob] = useState(null);
    const [errorMsg, setErrorMsg] = useState(null);

    const stop = () => {
        streamRef.current?.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
        try {
            recorderRef.current?.stop();
        } catch (_) {}
        recorderRef.current = null;
    };

    const stopCameraOnly = () => {
        streamRef.current?.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
    };

    useEffect(() => {
        if (!open) {
            stop();
            setStatus("idle");
            setPreviewBlob(null);
            setErrorMsg(null);
            setCueIdx(0);
        }
    }, [open]);

    const startCamera = async () => {
        setErrorMsg(null);
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                video: { facingMode: "user", width: { ideal: 640 }, height: { ideal: 480 } },
                audio: false,
            });
            streamRef.current = stream;
            if (videoRef.current) {
                videoRef.current.srcObject = stream;
                await videoRef.current.play().catch(() => {});
            }
            setStatus("ready");
        } catch (e) {
            setStatus("error");
            setErrorMsg(
                e?.name === "NotAllowedError"
                    ? "Camera permission denied. Enable it in your browser settings."
                    : "Couldn't access camera on this device.",
            );
        }
    };

    const record = () => {
        if (!streamRef.current || status !== "ready") return;
        chunksRef.current = [];
        const mimeType = MediaRecorder.isTypeSupported("video/webm;codecs=vp9")
            ? "video/webm;codecs=vp9"
            : MediaRecorder.isTypeSupported("video/webm")
                ? "video/webm"
                : "";
        const rec = new MediaRecorder(streamRef.current, mimeType ? { mimeType } : undefined);
        recorderRef.current = rec;
        rec.ondataavailable = (e) => {
            if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
        };
        rec.onstop = () => {
            const blob = new Blob(chunksRef.current, { type: mimeType || "video/webm" });
            setPreviewBlob(blob);
            setStatus("preview");
            // Turn the camera off once we have the recording (privacy + stops the live indicator).
            stopCameraOnly();
        };
        rec.start();
        setStatus("recording");

        // Advance cues + countdown
        const startedAt = Date.now();
        const tick = setInterval(() => {
            const elapsed = Date.now() - startedAt;
            const remaining = Math.max(0, Math.ceil((RECORD_MS - elapsed) / 1000));
            setCountdown(remaining);
            const nextCue = Math.min(CUES.length - 1, Math.floor((elapsed / RECORD_MS) * CUES.length));
            setCueIdx(nextCue);
            if (elapsed >= RECORD_MS) {
                clearInterval(tick);
                try {
                    rec.stop();
                } catch (_) {}
            }
        }, 100);
    };

    const upload = async () => {
        if (!previewBlob) return;
        setStatus("uploading");
        try {
            const fd = new FormData();
            const file = new File([previewBlob], `selfie-${Date.now()}.webm`, { type: previewBlob.type });
            fd.append("file", file);
            const { data } = await api.post("/verification/upload", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setStatus("done");
            toast.success("Submitted for review — we'll verify you shortly ✓");
            onVerified?.(data);
            setTimeout(() => onClose?.(), 1400);
        } catch (e) {
            const msg = e?.response?.data?.detail || "Upload failed — try again";
            setErrorMsg(msg);
            setStatus("preview");
            toast.error(msg);
        }
    };

    const retry = () => {
        setPreviewBlob(null);
        setCueIdx(0);
        setCountdown(RECORD_MS / 1000);
        setStatus("idle");
        // Camera was stopped after recording — spin it back up for another take.
        startCamera();
    };

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={onClose}
                    data-testid="verification-modal"
                    className="fixed inset-0 z-[80] bg-background/80 backdrop-blur-xl p-4 grid place-items-end sm:place-items-center"
                >
                    <motion.div
                        initial={{ y: "100%", opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: "100%", opacity: 0 }}
                        transition={{ type: "spring", stiffness: 260, damping: 30 }}
                        onClick={(e) => e.stopPropagation()}
                        className="w-full max-w-md rounded-4xl border-2 border-foreground bg-card p-5 pb-6 relative"
                    >
                        <div className="flex items-center justify-between mb-4">
                            <div>
                                <div className="text-[10px] font-black uppercase tracking-widest text-primary flex items-center gap-1">
                                    <ShieldCheck className="h-3 w-3" strokeWidth={3} /> verify
                                </div>
                                <div className="font-display text-2xl font-black leading-tight">
                                    Prove you're real
                                </div>
                            </div>
                            <button
                                onClick={onClose}
                                data-testid="verify-close-btn"
                                className="h-9 w-9 rounded-full hover:bg-accent grid place-items-center"
                            >
                                <X className="h-5 w-5" strokeWidth={2.5} />
                            </button>
                        </div>

                        {/* Camera / preview */}
                        <div className="relative aspect-square w-full rounded-3xl overflow-hidden bg-black border-2 border-foreground">
                            {status !== "preview" ? (
                                <video
                                    ref={videoRef}
                                    playsInline
                                    muted
                                    className="h-full w-full object-cover scale-x-[-1]"
                                    data-testid="verify-video-preview"
                                />
                            ) : (
                                <video
                                    src={previewBlob ? URL.createObjectURL(previewBlob) : ""}
                                    controls
                                    playsInline
                                    className="h-full w-full object-cover"
                                    data-testid="verify-video-playback"
                                />
                            )}

                            {status === "recording" && (
                                <>
                                    <div className="absolute top-3 left-3 flex items-center gap-1.5 rounded-full bg-primary text-primary-foreground px-3 py-1.5 text-[10px] font-black uppercase tracking-widest">
                                        <span className="h-2 w-2 rounded-full bg-white animate-pulse" />
                                        REC · {countdown}s
                                    </div>
                                    <div className="absolute bottom-0 inset-x-0 p-4 bg-gradient-to-t from-black/80 to-transparent text-center text-white">
                                        <div className="font-display text-lg font-black">{CUES[cueIdx]}</div>
                                    </div>
                                </>
                            )}

                            {(status === "idle" || status === "error") && (
                                <div className="absolute inset-0 grid place-items-center bg-black/60 text-white p-4 text-center">
                                    <div>
                                        <Video className="h-10 w-10 mx-auto mb-3 opacity-80" strokeWidth={2.5} />
                                        {status === "error" ? (
                                            <div className="text-sm" data-testid="verify-error-msg">{errorMsg}</div>
                                        ) : (
                                            <div className="text-sm opacity-90">Tap start to enable camera</div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>

                        <p className="text-xs text-muted-foreground mt-3">
                            We'll ask you to blink and turn your head. Never shown publicly — our team reviews it to give you a verified ✓ badge, usually within a day.
                        </p>

                        {/* Actions */}
                        <div className="mt-4 flex items-center gap-2">
                            {(status === "idle" || status === "error") && (
                                <Button
                                    onClick={startCamera}
                                    data-testid="verify-start-camera-btn"
                                    className="flex-1 h-12 rounded-2xl font-black lift"
                                >
                                    <Video className="mr-2 h-4 w-4" strokeWidth={2.5} /> Enable camera
                                </Button>
                            )}
                            {status === "ready" && (
                                <Button
                                    onClick={record}
                                    data-testid="verify-record-btn"
                                    className="flex-1 h-12 rounded-2xl font-black lift"
                                >
                                    Start recording (4s)
                                </Button>
                            )}
                            {status === "recording" && (
                                <Button
                                    disabled
                                    data-testid="verify-recording-state"
                                    className="flex-1 h-12 rounded-2xl font-black opacity-70"
                                >
                                    Recording…
                                </Button>
                            )}
                            {status === "preview" && (
                                <>
                                    <Button
                                        onClick={retry}
                                        variant="outline"
                                        data-testid="verify-retry-btn"
                                        className="h-12 rounded-2xl font-black"
                                    >
                                        <RefreshCw className="h-4 w-4 mr-1.5" /> Retry
                                    </Button>
                                    <Button
                                        onClick={upload}
                                        data-testid="verify-submit-btn"
                                        className="flex-1 h-12 rounded-2xl font-black lift"
                                    >
                                        Submit
                                    </Button>
                                </>
                            )}
                            {status === "uploading" && (
                                <Button
                                    disabled
                                    data-testid="verify-uploading-state"
                                    className="flex-1 h-12 rounded-2xl font-black opacity-70"
                                >
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Uploading…
                                </Button>
                            )}
                            {status === "done" && (
                                <Button
                                    disabled
                                    data-testid="verify-done-state"
                                    className="flex-1 h-12 rounded-2xl font-black bg-primary text-primary-foreground"
                                >
                                    <ShieldCheck className="h-4 w-4 mr-2" /> Submitted ✓
                                </Button>
                            )}
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
